// =============================================================================
// VINUM - Theme Provider
// Handles dark/light mode with system preference detection
// Matches Flutter ThemeProvider behavior exactly
// =============================================================================

import React, { useEffect, useLayoutEffect } from 'react';
import { useUIStore } from '@vinum/shared';

// Use useLayoutEffect on client, useEffect on server
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// -----------------------------------------------------------------------------
// Theme Provider Component
// -----------------------------------------------------------------------------

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const themeMode = useUIStore((state) => state.themeMode);
  const isDark = useUIStore((state) => state.isDark);
  const setThemeMode = useUIStore((state) => state.setThemeMode);

  // Apply dark class to document root
  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;

    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  // Listen for system preference changes when mode is 'system'
  useEffect(() => {
    if (themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      // Re-trigger the theme mode to recalculate isDark
      setThemeMode('system');
    };

    // Add listener with compatibility for older browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [themeMode, setThemeMode]);

  // Set initial theme on mount (handles SSR hydration)
  useEffect(() => {
    // Ensure isDark is correctly computed on mount
    setThemeMode(themeMode);
  }, []);

  return <>{children}</>;
};

// -----------------------------------------------------------------------------
// useTheme Hook
// Convenient hook for accessing theme state and actions
// -----------------------------------------------------------------------------

export function useTheme() {
  const themeMode = useUIStore((state) => state.themeMode);
  const isDark = useUIStore((state) => state.isDark);
  const setThemeMode = useUIStore((state) => state.setThemeMode);
  const toggleTheme = useUIStore((state) => state.toggleTheme);

  return {
    themeMode,
    isDark,
    setThemeMode,
    toggleTheme,
    // Convenience methods
    setLight: () => setThemeMode('light'),
    setDark: () => setThemeMode('dark'),
    setSystem: () => setThemeMode('system'),
  };
}

export default ThemeProvider;
